import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import moment from 'moment-timezone';

import {
  getStreamChatNotificationsApi,
  markAllStreamChatNotificationsReadApi,
  markAllNotificationsReadApi,
  markStreamChatNotificationReadApi,
  markNotificationReadApi,
} from '../../../../util/api';
import {
  applyMarkAllReadToCachedFeed,
  applyNotificationReadToCachedFeed,
  fetchNotificationsFeed,
} from '../../../../util/notificationsFeed';
import { useIntl } from '../../../../util/reactIntl';
import { Icons } from '../../../../components';
import {
  getNotificationTargetTxId,
  isUnhydratedStreamMessageNotification,
  isStreamMessageNotification,
  mergeNotificationFeed,
} from '../../../../util/notificationHelpers';
import {
  filterDisplayableNotifications,
  getUnreadCountForDisplayableNotifications,
  shouldDisplayNotification,
} from './notificationVisibility';
import { getNotificationNavigationTarget } from './notificationEventMeta';
import { getNotificationAppearance, getNotificationIcon } from './notificationStatusAppearance';
import { getLocalizedListingTitle, getLocalizedNotificationLine } from './notificationLocalization';
import { mapLegacyAttentionTransactionsToNotifications } from './legacyAttentionNotifications';
import { getNotificationTargetRole } from './notificationTargetRole';
import { resolveActiveRole } from '../../../../util/activeRole';

import css from './NotificationsPanel.module.css';

const NOTIFICATIONS_PAGE_SIZE = 20;
const UPDATE_EVENT_NAME = 'notifications_updated';
const CHANNEL_NAME = 'sceneaccess_notifications';
const EMPTY_LEGACY_ATTENTION_TRANSACTIONS = [];
const MAX_PLACEHOLDER_RESOLUTION_PAGES = 10;

const parseNotificationsSyncPayload = value => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    if (value === UPDATE_EVENT_NAME) {
      return {
        type: UPDATE_EVENT_NAME,
        forceNotificationsFresh: false,
        forceStreamFresh: false,
      };
    }

    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return {
        type: UPDATE_EVENT_NAME,
        forceNotificationsFresh: false,
        forceStreamFresh: false,
      };
    }
  }

  return value && typeof value === 'object' ? value : null;
};

const getTypeMeta = (notification, appearance) => ({ icon: getNotificationIcon(notification, appearance) });

const isMessageNotification = type => String(type || '').startsWith('message.');
const isBookingNotification = type => String(type || '').startsWith('booking.');

const getMessageNotificationsFromResponse = response => {
  const payload = response?.data || response || {};
  return Array.isArray(payload.notifications) ? payload.notifications : [];
};

const formatBookingLine = notification => {
  if (!notification?.bookingStartAt || !notification?.bookingEndAt) {
    return null;
  }

  const { bookingStartAt, bookingEndAt, bookingTimezone } = notification;
  const start = bookingTimezone
    ? moment.tz(bookingStartAt, bookingTimezone)
    : moment(bookingStartAt);
  const end = bookingTimezone ? moment.tz(bookingEndAt, bookingTimezone) : moment(bookingEndAt);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const sameDay = start.isSame(end, 'day');
  const startDate = start.format('MMM D, YYYY');
  const startTime = start.format('h:mm A');
  const endText = sameDay ? end.format('h:mm A') : `${end.format('MMM D, YYYY h:mm A')}`;

  return `${startDate} • ${startTime} – ${endText}`;
};

const getRelativeTime = occurredAt => {
  if (!occurredAt) {
    return '';
  }

  const value = moment(occurredAt);
  return value.isValid() ? value.fromNow() : '';
};

const appendNotifications = (currentNotifications = [], nextNotifications = []) => {
  if (!Array.isArray(nextNotifications) || nextNotifications.length === 0) {
    return currentNotifications;
  }

  const existingIds = new Set((currentNotifications || []).map(item => item?.$id).filter(Boolean));
  const uniqueNotifications = nextNotifications.filter(item => {
    const notificationId = item?.$id;

    if (!notificationId || existingIds.has(notificationId)) {
      return false;
    }

    existingIds.add(notificationId);
    return true;
  });

  return uniqueNotifications.length > 0
    ? [...(currentNotifications || []), ...uniqueNotifications]
    : currentNotifications;
};

const NotificationsPanel = props => {
  const intl = useIntl();

  const {
    currentUser,
    variant = 'desktop',
    isOpen,
    history,
    legacyAttentionTransactions = EMPTY_LEGACY_ATTENTION_TRANSACTIONS,
    onClose,
    onUnreadCountChange,
    onRoleSwitch,
    showDragHandle = true,
  } = props;

  const [notifications, setNotifications] = useState([]);
  const [messageNotifications, setMessageNotifications] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoadInProgress, setInitialLoadInProgress] = useState(false);
  const [loadMoreInProgress, setLoadMoreInProgress] = useState(false);
  const [markAllInProgress, setMarkAllInProgress] = useState(false);
  const [error, setError] = useState(null);
  const [roleSwitchError, setRoleSwitchError] = useState(false);
  const [activeNotificationId, setActiveNotificationId] = useState(null);

  const notificationsText = useMemo(
    () => ({
      close: intl.formatMessage({
        id: 'Notifications.close',
        defaultMessage: 'Close',
      }),
      empty: intl.formatMessage({
        id: 'Notifications.empty',
        defaultMessage: 'No notifications yet.',
      }),
      loadFailed: intl.formatMessage({
        id: 'Notifications.loadFailed',
        defaultMessage: 'Could not load notifications.',
      }),
      loading: intl.formatMessage({
        id: 'Notifications.loading',
        defaultMessage: 'Loading notifications...',
      }),
      loadingAriaLabel: intl.formatMessage({
        id: 'Notifications.loadingAriaLabel',
        defaultMessage: 'Loading notifications',
      }),
      loadingMore: intl.formatMessage({
        id: 'Notifications.loadingMore',
        defaultMessage: 'Loading more...',
      }),
      markAllRead: intl.formatMessage({
        id: 'Notifications.markAllRead',
        defaultMessage: 'Mark all read',
      }),
      markReadAriaLabel: intl.formatMessage({
        id: 'Notifications.markReadAriaLabel',
        defaultMessage: 'Mark as read',
      }),
      roleSwitchFailed: intl.formatMessage({
        id: 'TopbarDesktop.switchRoleFailed',
        defaultMessage: 'Could not switch role. Please try again.',
      }),
      title: intl.formatMessage({
        id: 'Notifications.title',
        defaultMessage: 'Notifications',
      }),
    }),
    [intl]
  );

  const scrollRef = useRef(null);
  const broadcastRef = useRef(null);
  const hiddenNotificationLogDoneRef = useRef(false);
  const hasLoadedCurrentScopeRef = useRef(false);
  const firstPageRequestRef = useRef(null);
  const loadMoreRequestRef = useRef(null);
  const requestEpochRef = useRef(0);
  const notificationActionsRef = useRef(new Set());
  const legacyAttentionItems = useMemo(
    () =>
      mapLegacyAttentionTransactionsToNotifications({
        transactions: legacyAttentionTransactions,
        currentUser,
      }),
    [currentUser, legacyAttentionTransactions]
  );
  const currentUserId = currentUser?.id?.uuid || null;
  const notificationsCacheScope = currentUserId;
  const isUserContextReady = !!currentUserId;

  const items = useMemo(() => {
    return mergeNotificationFeed({
      notifications,
      messageNotifications,
    });
  }, [messageNotifications, notifications]);
  const visibleItems = useMemo(
    () => items.filter(item => !isUnhydratedStreamMessageNotification(item)),
    [items]
  );
  const hiddenPlaceholderCount = items.length - visibleItems.length;

  const syncUnreadFromVisibleItems = useCallback(
    visibleItems => {
      if (typeof onUnreadCountChange !== 'function') {
        return;
      }

      onUnreadCountChange(getUnreadCountForDisplayableNotifications(visibleItems, currentUser));
    },
    [currentUser, onUnreadCountChange]
  );

  const filterAndMaybeLogHiddenNotifications = useCallback(
    notifications => {
      const list = Array.isArray(notifications) ? notifications : [];
      const filtered = filterDisplayableNotifications(list, currentUser);

      if (
        process.env.NODE_ENV === 'development' &&
        !hiddenNotificationLogDoneRef.current &&
        filtered.length !== list.length
      ) {
        const hiddenNotification = list.find(item => !shouldDisplayNotification(item, currentUser));
        if (hiddenNotification) {
          // Debug log once to confirm the payload identifier for hidden payment-pending events.
          // eslint-disable-next-line no-console
          console.info('[NotificationsPanel] Filtered notification sample:', hiddenNotification);
          hiddenNotificationLogDoneRef.current = true;
        }
      }

      return filtered;
    },
    [currentUser]
  );

  const publishUpdate = useCallback((options = {}) => {
    const payload = JSON.stringify({
      type: UPDATE_EVENT_NAME,
      forceNotificationsFresh: !!options.forceNotificationsFresh,
      forceStreamFresh: !!options.forceStreamFresh,
    });

    try {
      if (broadcastRef.current) {
        broadcastRef.current.postMessage(payload);
      }
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(UPDATE_EVENT_NAME, payload);
      }
    } catch (e) {
      // no-op
    }
  }, []);

  const replaceNotifications = useCallback(
    response => {
      setNotifications(filterAndMaybeLogHiddenNotifications(response?.items || []));
      setNextCursor(response?.nextCursor || null);
      setHasMore(!!response?.hasMore);
    },
    [filterAndMaybeLogHiddenNotifications]
  );

  const fetchDisplayableBatch = useCallback(
    async ({
      cursor = null,
      reason = 'panel-open',
      forceFresh = false,
      minVisibleCount = 1,
    } = {}) => {
      let nextCursorValue = cursor;
      let hasMoreValue = false;
      let collectedDocs = [];
      let guard = 0;

      do {
        const response = await fetchNotificationsFeed({
          limit: NOTIFICATIONS_PAGE_SIZE,
          cursor: nextCursorValue,
          reason,
          forceFresh: !nextCursorValue && forceFresh,
          cacheScope: notificationsCacheScope,
        });

        const docs = filterAndMaybeLogHiddenNotifications(response?.items || []);
        collectedDocs = [...collectedDocs, ...docs];
        nextCursorValue = response?.nextCursor || null;
        hasMoreValue = !!response?.hasMore;
        guard += 1;
      } while (
        collectedDocs.length < minVisibleCount &&
        hasMoreValue &&
        nextCursorValue &&
        guard < 10
      );

      return {
        items: collectedDocs,
        nextCursor: nextCursorValue,
        hasMore: hasMoreValue,
      };
    },
    [filterAndMaybeLogHiddenNotifications, notificationsCacheScope]
  );

  const getPendingStreamPlaceholderTxIds = useCallback(
    ({ notifications: notificationItems = [], messageNotifications: streamItems = [] }) => {
      return new Set(
        mergeNotificationFeed({
          notifications: notificationItems,
          messageNotifications: streamItems,
        })
          .filter(item => isUnhydratedStreamMessageNotification(item))
          .map(item => getNotificationTargetTxId(item))
          .filter(Boolean)
      );
    },
    []
  );

  const resolvePendingMessagePlaceholders = useCallback(
    async ({
      notifications: initialNotifications = [],
      messageNotifications: streamItems = [],
      nextCursor: initialNextCursor = null,
      hasMore: initialHasMore = false,
      reason = 'placeholder-resolution',
    } = {}) => {
      let nextCursorValue = initialNextCursor;
      let hasMoreValue = !!initialHasMore;
      let accumulatedNotifications = initialNotifications;
      let additionalNotifications = [];
      let pendingTxIds = getPendingStreamPlaceholderTxIds({
        notifications: accumulatedNotifications,
        messageNotifications: streamItems,
      });
      let guard = 0;

      while (
        pendingTxIds.size > 0 &&
        hasMoreValue &&
        nextCursorValue &&
        guard < MAX_PLACEHOLDER_RESOLUTION_PAGES
      ) {
        const response = await fetchDisplayableBatch({
          cursor: nextCursorValue,
          reason,
          minVisibleCount: 0,
        });
        const docs = response?.items || [];

        additionalNotifications = [...additionalNotifications, ...docs];
        accumulatedNotifications = [...accumulatedNotifications, ...docs];
        nextCursorValue = response?.nextCursor || null;
        hasMoreValue = !!response?.hasMore;
        pendingTxIds = getPendingStreamPlaceholderTxIds({
          notifications: accumulatedNotifications,
          messageNotifications: streamItems,
        });
        guard += 1;
      }

      return {
        additionalNotifications,
        nextCursor: nextCursorValue,
        hasMore: hasMoreValue,
      };
    },
    [fetchDisplayableBatch, getPendingStreamPlaceholderTxIds]
  );

  const fetchMessageNotifications = useCallback(async (options = {}) => {
    const response = await getStreamChatNotificationsApi({
      ...options,
      cacheScope: notificationsCacheScope,
    });
    return getMessageNotificationsFromResponse(response);
  }, [notificationsCacheScope]);

  const loadFirstPage = useCallback(
    async (options = {}) => {
      if (!isUserContextReady) {
        return Promise.resolve();
      }

      if (firstPageRequestRef.current) {
        return firstPageRequestRef.current;
      }

      // Collapse open/focus/visibility refreshes into one active request.
      const request = (async () => {
        const requestEpoch = requestEpochRef.current;
        setInitialLoadInProgress(true);
        setError(null);

        try {
          const [notificationsResult, messageNotificationsResult] = await Promise.allSettled([
            fetchDisplayableBatch({
              cursor: null,
              reason: options.reason || 'panel-open',
              forceFresh: !!options.forceNotificationsFresh,
              minVisibleCount: NOTIFICATIONS_PAGE_SIZE,
            }),
            fetchMessageNotifications({
              forceFresh: !!options.forceStreamFresh,
            }),
          ]);

          if (
            notificationsResult.status === 'rejected' &&
            messageNotificationsResult.status === 'rejected'
          ) {
            throw notificationsResult.reason || messageNotificationsResult.reason;
          }

          if (requestEpoch !== requestEpochRef.current) {
            return;
          }

          let nextNotifications = [];
          let nextCursorValue = null;
          let hasMoreValue = false;

          if (notificationsResult.status === 'fulfilled') {
            const response = notificationsResult.value;
            const shouldUseLegacyAttention =
              (response?.items || []).length === 0 && legacyAttentionItems.length > 0;
            const nextResponse = shouldUseLegacyAttention
              ? {
                  items: legacyAttentionItems,
                  nextCursor: null,
                  hasMore: false,
                }
              : response;

            nextNotifications = nextResponse?.items || [];
            nextCursorValue = nextResponse?.nextCursor || null;
            hasMoreValue = !!nextResponse?.hasMore;
            replaceNotifications(nextResponse);
          } else {
            console.error(notificationsResult.reason); // eslint-disable-line no-console
            nextNotifications = legacyAttentionItems;
            nextCursorValue = null;
            hasMoreValue = false;
            replaceNotifications({
              items: legacyAttentionItems,
              nextCursor: null,
              hasMore: false,
            });
          }

          if (messageNotificationsResult.status === 'fulfilled') {
            const nextMessageNotifications = messageNotificationsResult.value;
            setMessageNotifications(nextMessageNotifications);

            const pendingPlaceholderTxIds = getPendingStreamPlaceholderTxIds({
              notifications: nextNotifications,
              messageNotifications: nextMessageNotifications,
            });

            if (pendingPlaceholderTxIds.size > 0 && hasMoreValue && nextCursorValue) {
              setLoadMoreInProgress(true);
              setInitialLoadInProgress(false);

              try {
                const placeholderResolution = await resolvePendingMessagePlaceholders({
                  notifications: nextNotifications,
                  messageNotifications: nextMessageNotifications,
                  nextCursor: nextCursorValue,
                  hasMore: hasMoreValue,
                  reason: 'placeholder-resolution',
                });

                if (requestEpoch !== requestEpochRef.current) {
                  return;
                }

                if ((placeholderResolution?.additionalNotifications || []).length > 0) {
                  setNotifications(prev =>
                    appendNotifications(prev, placeholderResolution.additionalNotifications)
                  );
                }
                setNextCursor(placeholderResolution?.nextCursor || null);
                setHasMore(!!placeholderResolution?.hasMore);
              } finally {
                if (requestEpoch === requestEpochRef.current) {
                  setLoadMoreInProgress(false);
                }
              }
            }
          } else {
            console.error(messageNotificationsResult.reason); // eslint-disable-line no-console
          }

          hasLoadedCurrentScopeRef.current = true;
        } catch (e) {
          if (requestEpoch !== requestEpochRef.current) {
            return;
          }
          setError(e);
        } finally {
          if (requestEpoch === requestEpochRef.current) {
            setInitialLoadInProgress(false);
          }
        }
      })();

      firstPageRequestRef.current = request.finally(() => {
        firstPageRequestRef.current = null;
      });

      return firstPageRequestRef.current;
    },
    [
      fetchDisplayableBatch,
      fetchMessageNotifications,
      getPendingStreamPlaceholderTxIds,
      isUserContextReady,
      legacyAttentionItems,
      replaceNotifications,
      resolvePendingMessagePlaceholders,
    ]
  );

  const loadMore = useCallback(async () => {
    if (!isUserContextReady) {
      return Promise.resolve();
    }

    if (loadMoreRequestRef.current) {
      return loadMoreRequestRef.current;
    }

    if (!hasMore || loadMoreInProgress || !nextCursor) {
      return Promise.resolve();
    }

    const request = (async () => {
      const requestEpoch = requestEpochRef.current;
      setLoadMoreInProgress(true);
      setError(null);

      try {
        const response = await fetchDisplayableBatch({
          cursor: nextCursor,
          reason: 'load-more',
          minVisibleCount: 0,
        });

        if (requestEpoch !== requestEpochRef.current) {
          return;
        }

        const docs = response?.items || [];
        const nextNotifications = [...notifications, ...docs];
        let nextCursorValue = response?.nextCursor || null;
        let hasMoreValue = !!response?.hasMore;
        let additionalNotifications = docs;

        const pendingPlaceholderTxIds = getPendingStreamPlaceholderTxIds({
          notifications: nextNotifications,
          messageNotifications,
        });

        if (pendingPlaceholderTxIds.size > 0 && hasMoreValue && nextCursorValue) {
          const placeholderResolution = await resolvePendingMessagePlaceholders({
            notifications: nextNotifications,
            messageNotifications,
            nextCursor: nextCursorValue,
            hasMore: hasMoreValue,
            reason: 'placeholder-resolution',
          });

          if (requestEpoch !== requestEpochRef.current) {
            return;
          }

          additionalNotifications = appendNotifications(
            additionalNotifications,
            placeholderResolution?.additionalNotifications || []
          );
          nextCursorValue = placeholderResolution?.nextCursor || null;
          hasMoreValue = !!placeholderResolution?.hasMore;
        }

        setNotifications(prev => appendNotifications(prev, additionalNotifications));
        setNextCursor(nextCursorValue);
        setHasMore(hasMoreValue);
      } catch (e) {
        if (requestEpoch !== requestEpochRef.current) {
          return;
        }
        setError(e);
      } finally {
        if (requestEpoch === requestEpochRef.current) {
          setLoadMoreInProgress(false);
        }
      }
    })();

    loadMoreRequestRef.current = request.finally(() => {
      loadMoreRequestRef.current = null;
    });

    return loadMoreRequestRef.current;
  }, [
    fetchDisplayableBatch,
    getPendingStreamPlaceholderTxIds,
    hasMore,
    isUserContextReady,
    loadMoreInProgress,
    messageNotifications,
    nextCursor,
    notifications,
    resolvePendingMessagePlaceholders,
  ]);

  useEffect(() => {
    requestEpochRef.current += 1;
    firstPageRequestRef.current = null;
    loadMoreRequestRef.current = null;
    hiddenNotificationLogDoneRef.current = false;
    hasLoadedCurrentScopeRef.current = false;
    setNotifications([]);
    setMessageNotifications([]);
    setNextCursor(null);
    setHasMore(false);
    setError(null);
    setInitialLoadInProgress(false);
    setLoadMoreInProgress(false);
  }, [notificationsCacheScope]);

  useEffect(() => {
    syncUnreadFromVisibleItems(items);
  }, [items, syncUnreadFromVisibleItems]);

  const setNotificationReadOptimistically = useCallback(notificationId => {
    setNotifications(prev =>
      prev.map(item => {
        if (item.$id !== notificationId || item.isRead) {
          return item;
        }

        return {
          ...item,
          isRead: true,
          readAt: item.readAt || new Date().toISOString(),
        };
      })
    );
  }, []);

  const setMessageNotificationReadOptimistically = useCallback(notification => {
    const txId = getNotificationTargetTxId(notification);

    if (!txId) {
      return;
    }

    setMessageNotifications(prev =>
      prev.map(item => {
        if (getNotificationTargetTxId(item) !== txId || Number(item?.unreadCount || 0) === 0) {
          return item;
        }

        return {
          ...item,
          hasUnread: false,
          unreadCount: 0,
        };
      })
    );
  }, []);

  const markSingleRead = useCallback(
    async notification => {
      if (!notification || notification.isRead || notification.isLegacyFallback) {
        return;
      }

      if (isStreamMessageNotification(notification)) {
        setMessageNotificationReadOptimistically(notification);

        try {
          const response = await markStreamChatNotificationReadApi(
            getNotificationTargetTxId(notification)
          );
          const updatedNotification = response?.data?.notification || response?.notification;
          const txId = getNotificationTargetTxId(notification);

          setMessageNotifications(prev =>
            prev.map(item => {
              if (getNotificationTargetTxId(item) !== txId) {
                return item;
              }

              return {
                ...item,
                ...(updatedNotification || {}),
                hasUnread: false,
                unreadCount: 0,
              };
            })
          );

          publishUpdate({ forceStreamFresh: true });
        } catch (e) {
          await loadFirstPage({
            forceStreamFresh: true,
            reason: 'mutation-recovery',
          });
        }

        return;
      }

      setNotificationReadOptimistically(notification.$id);

      try {
        const response = await markNotificationReadApi(notification.$id);
        const updatedNotification = response?.data?.notification || response?.notification;
        const unreadCount = response?.data?.state?.unreadCount ?? response?.state?.unreadCount;

        applyNotificationReadToCachedFeed({
          notificationId: notification.$id,
          updatedNotification,
          unreadCount,
          cacheScope: notificationsCacheScope,
        });

        setNotifications(prev =>
          prev.map(item => {
            if (item.$id !== notification.$id) {
              return item;
            }
            return updatedNotification || { ...item, isRead: true };
          })
        );

        publishUpdate({ forceNotificationsFresh: true });
      } catch (e) {
        await loadFirstPage({
          forceNotificationsFresh: true,
          reason: 'mutation-recovery',
        });
      }
    },
    [
      loadFirstPage,
      notificationsCacheScope,
      publishUpdate,
      setMessageNotificationReadOptimistically,
      setNotificationReadOptimistically,
    ]
  );

  const handleCardClick = useCallback(
    notification => {
      if (!notification) {
        return;
      }

      const targetUrl = getNotificationNavigationTarget(notification);
      const notificationActionId = notification.$id || targetUrl;
      if (!notificationActionId || notificationActionsRef.current.has(notificationActionId)) {
        return;
      }

      notificationActionsRef.current.add(notificationActionId);
      setActiveNotificationId(notificationActionId);
      setRoleSwitchError(false);

      if (!notification.isRead && !notification.isLegacyFallback) {
        markSingleRead(notification);
      }

      const didNavigate = !!targetUrl && !!history;
      const releaseNotificationAction = () => {
        notificationActionsRef.current.delete(notificationActionId);
        setActiveNotificationId(currentId =>
          currentId === notificationActionId ? null : currentId
        );
      };
      const handleRoleSwitchFailure = roleSwitchFailure => {
        // eslint-disable-next-line no-console
        console.error('Failed to switch role for notification navigation', roleSwitchFailure);
        setRoleSwitchError(true);
        releaseNotificationAction();
      };
      const completeNavigation = () => {
        if (didNavigate) {
          history.push(targetUrl);
        } else {
          releaseNotificationAction();
        }

        // Mobile notification sheets are URL-state driven. Closing with the mobile onClose handler
        // after navigation can push the previous page URL back on top of the booking route.
        if (typeof onClose === 'function' && !(variant === 'mobile' && didNavigate)) {
          onClose();
        }
      };

      if (!didNavigate) {
        completeNavigation();
        return;
      }

      const targetRole = getNotificationTargetRole(notification);
      const currentRole = resolveActiveRole(currentUser);
      if (!targetRole || targetRole === currentRole) {
        completeNavigation();
        return;
      }

      if (typeof onRoleSwitch !== 'function') {
        handleRoleSwitchFailure(new Error('Notification role switch handler is unavailable'));
        return;
      }

      try {
        const roleSwitchResult = onRoleSwitch(targetRole, notification);
        if (roleSwitchResult && typeof roleSwitchResult.then === 'function') {
          roleSwitchResult
            .then(updatedUser => {
              if (!updatedUser?.id) {
                throw new Error('Notification role update did not return a current user');
              }
              completeNavigation();
            })
            .catch(handleRoleSwitchFailure);
        } else if (roleSwitchResult?.id) {
          completeNavigation();
        } else {
          handleRoleSwitchFailure(
            new Error('Notification role update did not return a current user')
          );
        }
      } catch (roleSwitchFailure) {
        handleRoleSwitchFailure(roleSwitchFailure);
      }
    },
    [currentUser, history, markSingleRead, onClose, onRoleSwitch, variant]
  );

  const handleMarkAllRead = useCallback(async () => {
    if (markAllInProgress) {
      return;
    }

    const unreadNotifications = notifications.filter(
      item => !item?.isRead && !item?.isLegacyFallback
    );
    const unreadMessageNotifications = messageNotifications.filter(
      item => Number(item?.unreadCount || 0) > 0
    );

    if (unreadNotifications.length === 0 && unreadMessageNotifications.length === 0) {
      return;
    }

    setMarkAllInProgress(true);
    setNotifications(prev =>
      prev.map(item =>
        item?.isLegacyFallback
          ? item
          : {
              ...item,
              isRead: true,
              readAt: item.readAt || new Date().toISOString(),
            }
      )
    );
    setMessageNotifications(prev =>
      prev.map(item => ({
        ...item,
        hasUnread: false,
        unreadCount: 0,
      }))
    );

    try {
      const [notificationsResponse] = await Promise.all([
        unreadNotifications.length > 0 ? markAllNotificationsReadApi() : Promise.resolve(null),
        unreadMessageNotifications.length > 0
          ? markAllStreamChatNotificationsReadApi()
          : Promise.resolve(null),
      ]);

      applyMarkAllReadToCachedFeed({
        unreadCount:
          notificationsResponse?.data?.state?.unreadCount ??
          notificationsResponse?.state?.unreadCount ??
          0,
        cacheScope: notificationsCacheScope,
      });

      publishUpdate({
        forceNotificationsFresh: unreadNotifications.length > 0,
        forceStreamFresh: unreadMessageNotifications.length > 0,
      });
    } catch (e) {
      await loadFirstPage({
        forceNotificationsFresh: unreadNotifications.length > 0,
        forceStreamFresh: unreadMessageNotifications.length > 0,
        reason: 'mutation-recovery',
      });
    } finally {
      setMarkAllInProgress(false);
    }
  }, [
    loadFirstPage,
    markAllInProgress,
    messageNotifications,
    notifications,
    notificationsCacheScope,
    publishUpdate,
  ]);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadMoreInProgress || !hasMore) {
      return;
    }

    const threshold = 140;
    const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceToBottom <= threshold) {
      loadMore();
    }
  }, [hasMore, loadMore, loadMoreInProgress]);

  useEffect(() => {
    if (!isOpen) {
      notificationActionsRef.current.clear();
      setActiveNotificationId(null);
      setRoleSwitchError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isUserContextReady) {
      return;
    }
    const shouldForceFreshInitialLoad = !hasLoadedCurrentScopeRef.current;

    loadFirstPage({
      reason: 'panel-open',
      forceNotificationsFresh: shouldForceFreshInitialLoad,
      forceStreamFresh: shouldForceFreshInitialLoad,
    });
  }, [isOpen, isUserContextReady, loadFirstPage]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if ('BroadcastChannel' in window) {
      broadcastRef.current = new BroadcastChannel(CHANNEL_NAME);
    }

    const handleBroadcast = event => {
      const payload = parseNotificationsSyncPayload(event?.data);
      if (payload?.type === UPDATE_EVENT_NAME && isOpen) {
        loadFirstPage({
          forceNotificationsFresh: !!payload?.forceNotificationsFresh,
          forceStreamFresh: !!payload?.forceStreamFresh,
          reason: 'sync-event',
        });
      }
    };

    const handleStorage = event => {
      if (event?.key !== UPDATE_EVENT_NAME) {
        return;
      }

      const payload = parseNotificationsSyncPayload(event?.newValue);
      if (isOpen) {
        loadFirstPage({
          forceNotificationsFresh: !!payload?.forceNotificationsFresh,
          forceStreamFresh: !!payload?.forceStreamFresh,
          reason: 'sync-event',
        });
      }
    };

    if (broadcastRef.current) {
      broadcastRef.current.addEventListener('message', handleBroadcast);
    }

    window.addEventListener('storage', handleStorage);

    return () => {
      if (broadcastRef.current) {
        broadcastRef.current.removeEventListener('message', handleBroadcast);
        broadcastRef.current.close();
        broadcastRef.current = null;
      }
      window.removeEventListener('storage', handleStorage);
    };
  }, [isOpen, loadFirstPage]);

  const panelClass = classNames(css.panel, {
    [css.panelMobile]: variant === 'mobile',
    [css.panelDesktop]: variant === 'desktop',
  });

  const hasItems = visibleItems.length > 0;

  const content = useMemo(() => {
    if (initialLoadInProgress || (isOpen && !isUserContextReady)) {
      return (
        <div
          className={css.loaderStatus}
          role="status"
          aria-live="polite"
          aria-label={notificationsText.loading}
        >
          <span className="loader" aria-label={notificationsText.loadingAriaLabel}></span>
        </div>
      );
    }

    if (error) {
      return <div className={css.feedback}>{notificationsText.loadFailed}</div>;
    }

    if (!hasItems) {
      if (loadMoreInProgress && hiddenPlaceholderCount > 0) {
        return (
          <div
            className={css.loaderStatus}
            role="status"
            aria-live="polite"
            aria-label={notificationsText.loadingMore}
          >
            <span className="loader" aria-label={notificationsText.loadingAriaLabel}></span>
          </div>
        );
      }

      return <div className={css.feedback}>{notificationsText.empty}</div>;
    }

    return (
      <div
        className={css.list}
        ref={scrollRef}
        onScroll={onScroll}
        data-body-scroll-lock-ignore={variant === 'mobile' ? 'true' : undefined}
      >
        {visibleItems.map(item => {
          const appearance = getNotificationAppearance(item);
          const typeMeta = getTypeMeta(item, appearance);
          const bookingLine = formatBookingLine(item);
          const relativeTime = getRelativeTime(item.occurredAt);
          const isMessage = isMessageNotification(item.type);
          const isBooking = isBookingNotification(item.type);
          const isUnread = !item.isRead;
          const notificationType = isMessage ? 'message' : isBooking ? 'booking' : 'unknown';
          const notificationStyle = {
            '--notificationAccentColor': appearance?.color,
            '--notificationAccentSoftColor': appearance?.softColor,
          };
          const localizedHeadline = getLocalizedNotificationLine({
            notification: item,
            field: 'headline',
            intl,
          });
          const localizedListingName = getLocalizedListingTitle({
            notification: item,
            locale: intl.locale,
          });
          const localizedSnippet = getLocalizedNotificationLine({
            notification: item,
            field: 'snippet',
            intl,
          });
          const targetRole = getNotificationTargetRole(item);
          const notificationActionInProgress = activeNotificationId === item.$id;

          return (
            <button
              key={item.$id}
              type="button"
              className={classNames(
                'NotificationItem',
                {
                  'NotificationItem--message': isMessage,
                  'NotificationItem--booking': isBooking,
                  'NotificationItem--unread': isUnread,
                  'NotificationItem--read': !isUnread,
                },
                css.item,
                {
                  [css.itemMessage]: isMessage,
                  [css.itemBooking]: isBooking,
                  [css.itemUnread]: isUnread,
                  [css.itemRead]: !isUnread,
                }
              )}
              onClick={() => handleCardClick(item)}
              style={notificationStyle}
              data-testid={`notification-item-${item.$id}`}
              data-notification-type={notificationType}
              data-notification-icon={typeMeta.icon}
              data-notification-status={appearance?.status || 'unknown'}
              data-notification-unread={isUnread ? 'true' : 'false'}
              data-notification-target-role={targetRole || 'unknown'}
              aria-busy={notificationActionInProgress ? 'true' : undefined}
            >
              <div className={css.iconWrap}>
                <Icons name={typeMeta.icon} />
              </div>

              <div className={css.contentWrap}>
                <div className={css.headlineRow}>
                  <span className={css.headline}>{localizedHeadline}</span>
                  {!item.isRead ? <span className={css.unreadDot} /> : null}
                </div>
                {localizedListingName ? (
                  <div className={css.listingName}>{localizedListingName}</div>
                ) : null}
                {isMessage && localizedSnippet ? (
                  <div className={css.snippet}>{localizedSnippet}</div>
                ) : null}
                {bookingLine ? <div className={css.bookingLine}>{bookingLine}</div> : null}
                {relativeTime ? <div className={css.time}>{relativeTime}</div> : null}{' '}
              </div>

              <div className={css.actions}>
                {!item.isRead && !item.isLegacyFallback ? (
                  <button
                    type="button"
                    className={css.markReadButton}
                    onClick={event => {
                      event.preventDefault();
                      event.stopPropagation();
                      markSingleRead(item);
                    }}
                    aria-label={notificationsText.markReadAriaLabel}
                    data-testid={`notification-mark-read-${item.$id}`}
                  >
                    <span className={css.markReadGlyph}>
                      <Icons name="tick" />
                    </span>
                  </button>
                ) : null}
                <span className={css.chevron}>
                  <Icons name="rightAngle" />
                </span>
              </div>
            </button>
          );
        })}

        {loadMoreInProgress ? (
          <div
            className={css.footerLoader}
            role="status"
            aria-live="polite"
            aria-label={notificationsText.loadingMore}
          >
            <span className="loader" aria-label={notificationsText.loadingAriaLabel}></span>
          </div>
        ) : null}
      </div>
    );
  }, [
    activeNotificationId,
    error,
    handleCardClick,
    hasItems,
    hiddenPlaceholderCount,
    isOpen,
    isUserContextReady,
    initialLoadInProgress,
    intl,
    loadMoreInProgress,
    markSingleRead,
    notificationsText.empty,
    notificationsText.loadFailed,
    notificationsText.loading,
    notificationsText.loadingAriaLabel,
    notificationsText.loadingMore,
    notificationsText.markReadAriaLabel,
    onScroll,
    visibleItems,
  ]);

  return (
    <div
      className={panelClass}
      data-testid={
        variant === 'mobile' ? 'notifications-panel-mobile' : 'notifications-panel-desktop'
      }
    >
      {variant === 'mobile' && showDragHandle ? <div className={css.dragHandle} /> : null}

      <div className={css.header}>
        <h3 className={css.title}>{notificationsText.title}</h3>

        <div className={css.headerActions}>
          <button
            type="button"
            className={css.markAllButton}
            onClick={handleMarkAllRead}
            disabled={markAllInProgress}
            data-testid="notifications-mark-all-read"
          >
            {notificationsText.markAllRead}
          </button>
          {variant === 'mobile' ? (
            <button
              type="button"
              className={css.closeButton}
              onClick={onClose}
              aria-label={notificationsText.close}
            >
              <Icons name="close" />
            </button>
          ) : null}
        </div>
      </div>

      {roleSwitchError ? (
        <div className={css.actionError} role="alert">
          {notificationsText.roleSwitchFailed}
        </div>
      ) : null}

      {content}
    </div>
  );
};

export default NotificationsPanel;
